if (!customElements.get('birthstone-configurator')) {
  customElements.define(
    'birthstone-configurator',
    class BirthstoneConfigurator extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        if (!this.dataset.productHandle || this.dataset.productHandle !== this.dataset.targetProductHandle) return;

        this.dataset.initialized = 'true';
        this.form = this.closest('form');
        this.drawer = this.querySelector('.birthstone-configurator__drawer');
        this.panel = this.drawer?.querySelector('.birthstone-configurator__panel');
        this.openButton = this.querySelector('[data-open-birthstone-drawer]');
        this.closeButtons = Array.from(this.querySelectorAll('[data-close-birthstone-drawer]'));
        this.closeButton = this.drawer?.querySelector('.birthstone-configurator__close');
        this.confirmButton = this.querySelector('[data-confirm-birthstones]');
        this.submitButton = this.form?.querySelector('[type="submit"][name="add"]');
        this.variantInput = this.form?.querySelector('input[name="id"]');
        this.imageProperty = this.querySelector('[data-custom-image-url]');
        this.status = this.querySelector('[data-birthstone-status]');
        this.requestSequence = 0;
        this.returnFocus = null;
        this.lastShownImageUrl = null;
        this.applyingPreview = false;
        this.variantData = this.readVariantData();

        // Escape product scroll-trigger transforms that jitter fixed drawers.
        if (this.drawer && this.drawer.parentElement !== document.body) {
          document.body.appendChild(this.drawer);
          this.closeButtons = Array.from(this.drawer.querySelectorAll('[data-close-birthstone-drawer]'));
          this.closeButton = this.drawer.querySelector('.birthstone-configurator__close');
          this.confirmButton = this.drawer.querySelector('[data-confirm-birthstones]');
          this.status = this.drawer.querySelector('[data-birthstone-status]');
          this.panel = this.drawer.querySelector('.birthstone-configurator__panel');
        }

        this.onOpen = this.open.bind(this);
        this.onClose = this.close.bind(this);
        this.onKeyUp = this.handleKeyUp.bind(this);
        this.onOptionChange = this.handleOptionChange.bind(this);
        this.onSubmit = this.handleSubmit.bind(this);
        this.onVariantChange = this.handleVariantChange.bind(this);

        this.openButton?.addEventListener('click', this.onOpen);
        this.closeButtons.forEach((button) => button.addEventListener('click', this.onClose));
        this.confirmButton?.addEventListener('click', this.onClose);
        this.drawer?.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.addEventListener('change', this.onOptionChange);
        });
        this.form?.addEventListener('submit', this.onSubmit, true);
        this.variantInput?.addEventListener('change', this.onVariantChange);

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.hideNativeStoneOptions();
        this.observeGloboStoneOptions();
        this.observeProductMedia();
        this.updateState();
      }

      disconnectedCallback() {
        this.unsubscribeVariant?.();
        this.globoObserver?.disconnect();
        this.mediaObserver?.disconnect();
        document.removeEventListener('keyup', this.onKeyUp);
        if (this.drawer?.parentElement === document.body) {
          this.drawer.remove();
        }
      }

      query(selector) {
        return this.querySelector(selector) || this.drawer?.querySelector(selector) || null;
      }

      queryAll(selector) {
        const hostMatches = Array.from(this.querySelectorAll(selector));
        if (!this.drawer || this.contains(this.drawer)) return hostMatches;
        return hostMatches.concat(Array.from(this.drawer.querySelectorAll(selector)));
      }

      isStoneOptionName(name) {
        const normalized = this.normalizeValue(name);
        return (
          normalized.includes('piedra') ||
          normalized.includes('pedra') ||
          normalized.includes('kamien') ||
          normalized.includes('stone') ||
          normalized.includes('birthstone')
        );
      }

      hideNativeStoneOptions() {
        const pickerWrap = this.closest('product-info')?.querySelector('.birthstone-native-variant-picker');
        if (!pickerWrap) return;

        pickerWrap.querySelectorAll('.swatch--gl').forEach((group) => {
          const optionName =
            group.querySelector('.name-option')?.getAttribute('data-name') ||
            group.querySelector('.name-option, legend')?.textContent ||
            '';
          group.classList.toggle('birthstone-stone-option', this.isStoneOptionName(optionName));
          if (this.isStoneOptionName(optionName)) {
            group.hidden = true;
            group.style.display = 'none';
          }
        });
      }

      observeGloboStoneOptions() {
        const pickerWrap = this.closest('product-info')?.querySelector('.birthstone-native-variant-picker');
        if (!pickerWrap || typeof MutationObserver === 'undefined') return;

        this.globoObserver = new MutationObserver(() => this.hideNativeStoneOptions());
        this.globoObserver.observe(pickerWrap, { childList: true, subtree: true });
        // GLO often injects after first paint.
        [100, 500, 1500, 3000].forEach((delay) => {
          setTimeout(() => this.hideNativeStoneOptions(), delay);
        });
      }

      readVariantData() {
        try {
          return JSON.parse(this.querySelector('[data-birthstone-variants]')?.textContent || '{}');
        } catch (error) {
          console.error('Invalid birthstone variant configuration.', error);
          return {};
        }
      }

      get selections() {
        return [1, 2].map((index) => {
          const input = this.query(`[data-option-index="${index}"] input:checked`);
          return input
            ? {
                value: input.value,
                label: input.dataset.monthLabel || input.value,
              }
            : null;
        });
      }

      get isComplete() {
        return this.selections.every(Boolean);
      }

      get isDrawerOpen() {
        return this.drawer?.hasAttribute('open');
      }

      get currentVariant() {
        return this.variantData[this.variantInput?.value] || {
          prefix: this.dataset.imagePrefix,
          available: !this.variantInput?.disabled,
        };
      }

      open(event) {
        event?.preventDefault();
        if (!this.drawer || this.isDrawerOpen) return;

        this.returnFocus = event?.currentTarget || document.activeElement;
        this.openButton?.setAttribute('aria-expanded', 'true');
        this.lockScroll();
        this.drawer.hidden = false;
        document.addEventListener('keyup', this.onKeyUp);

        if (this.isComplete) this.updateCombinationImage();

        // Force the off-screen frame before opening so the slide transition runs once.
        if (this.panel) void this.panel.offsetWidth;
        this.drawer.setAttribute('open', '');

        requestAnimationFrame(() => {
          if (typeof trapFocus === 'function' && this.panel) {
            trapFocus(this.panel, this.closeButton || this.panel);
          } else {
            this.closeButton?.focus({ preventScroll: true });
          }
        });
      }

      close(event) {
        event?.preventDefault();
        if (!this.isDrawerOpen) return;
        if (!this.isComplete && event?.currentTarget === this.confirmButton) return;

        this.openButton?.setAttribute('aria-expanded', 'false');
        this.drawer.removeAttribute('open');
        this.drawer.hidden = true;
        document.removeEventListener('keyup', this.onKeyUp);
        this.unlockScroll();

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.returnFocus || this.openButton);
        } else {
          this.returnFocus?.focus({ preventScroll: true });
        }
        this.returnFocus = null;
      }

      handleKeyUp(event) {
        if (event.code?.toUpperCase() === 'ESCAPE') this.close();
      }

      lockScroll() {
        const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
        document.documentElement.style.setProperty(
          '--birthstone-scrollbar-compensation',
          `${scrollbarWidth}px`
        );
        document.body.classList.add('birthstone-configurator-open');
      }

      unlockScroll() {
        document.body.classList.remove('birthstone-configurator-open');
        document.documentElement.style.removeProperty('--birthstone-scrollbar-compensation');
      }

      handleOptionChange(event) {
        const group = event.target.closest('[data-option-index]');
        group?.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.setAttribute('aria-checked', String(input.checked));
        });

        const index = group?.dataset.optionIndex;
        const property = this.query(`[data-birthstone-property="${index}"]`);
        if (property) property.value = event.target.value;

        this.updateState();
        this.prefetchCurrentRow();

        if (!this.isComplete) return;

        // Keep native options in sync for the correct variant ID / gallery,
        // but the drawer preview is driven only by our selections below.
        this.syncAllNativeOptions();
        this.updateCombinationImage();
      }

      handleSubmit(event) {
        if (this.isComplete) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.status) this.status.textContent = this.dataset.requiredMessage || '';
        this.open();
      }

      handleVariantChange() {
        this.updateState();

        if (this.isComplete) {
          this.scheduleCombinationImageUpdate(0);
          return;
        }

        if (this.isDrawerOpen) return;

        this.clearCombinationImage();
      }

      scheduleCombinationImageUpdate(delay = 0) {
        clearTimeout(this.previewSyncTimer);
        this.previewSyncTimer = setTimeout(() => this.updateCombinationImage(), delay);
      }

      updateState() {
        const selections = this.selections;
        const count = selections.filter(Boolean).length;

        this.queryAll('[data-customisation-count]').forEach((element) => {
          element.textContent = `${count}/2`;
        });

        if (this.confirmButton) this.confirmButton.disabled = count !== 2;

        if (this.submitButton) {
          const canPurchase = count === 2 && this.currentVariant.available !== false;
          this.submitButton.toggleAttribute('disabled', !canPurchase);
          if (canPurchase) {
            this.submitButton.removeAttribute('aria-disabled');
          } else {
            this.submitButton.setAttribute('aria-disabled', 'true');
          }
        }

        const summary = this.query('[data-customisation-summary]');
        if (summary && count > 0) {
          summary.textContent = selections.filter(Boolean).map((selection) => selection.label).join(' + ');
        }

        if (this.status && count === 2) this.status.textContent = '';
      }

      buildFilename() {
        const selections = this.selections;
        const prefix = this.currentVariant.prefix || this.dataset.imagePrefix;
        if (!prefix || !selections[0] || !selections[1]) return null;

        const sanitize = (value) => this.comboSanitize(value);

        return `${sanitize(prefix)}-${sanitize(selections[0].value)}-${sanitize(selections[1].value)}.jpg`;
      }

      buildImageUrl(filename) {
        const baseUrl = this.dataset.imageBaseUrl?.trim();
        if (!filename || !baseUrl) return null;
        if (baseUrl.includes('{filename}')) return baseUrl.replace('{filename}', filename);
        return `${baseUrl.replace(/\/+$/, '')}/${filename}`;
      }

      normalizeValue(value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
      }

      matchesSelection(optionValue, selection) {
        if (!selection) return false;
        const option = this.normalizeValue(optionValue);
        const optionMonth = option.split(/[-–—|/]/)[0].trim();
        const label = this.normalizeValue(selection.label);
        const value = this.normalizeValue(selection.value);
        const aliases = this.monthAliases(value).concat(this.monthAliases(label));

        return (
          option.startsWith(label) ||
          option.startsWith(value) ||
          option.includes(label) ||
          option.includes(value) ||
          aliases.includes(optionMonth) ||
          aliases.some((alias) => option.startsWith(alias) || option.includes(alias))
        );
      }

      monthAliases(value) {
        const key = this.normalizeValue(value).split(/[-–—|/]/)[0].trim();
        const groups = [
          ['january', 'enero', 'janeiro'],
          ['february', 'febrero', 'fevereiro'],
          ['march', 'marzo', 'marco'],
          ['april', 'abril'],
          ['may', 'mayo', 'maio'],
          ['june', 'junio', 'junho'],
          ['july', 'julio', 'julho'],
          ['august', 'agosto'],
          ['september', 'septiembre', 'setembro'],
          ['october', 'octubre', 'outubro'],
          ['november', 'noviembre', 'novembro'],
          ['december', 'diciembre', 'dezembro'],
        ];
        const group = groups.find((months) => months.includes(key));
        return group || (key ? [key] : []);
      }

      getOptionGroupLabel(optionGroup) {
        return (
          optionGroup?.querySelector('legend.form__label, label.form__label, .form__label')?.textContent || ''
        ).trim();
      }

      isStoneOptionLabel(label) {
        const normalized = this.normalizeValue(label);
        return (
          normalized.includes('piedra') ||
          normalized.includes('pedra') ||
          normalized.includes('kamien') ||
          normalized.includes('stone') ||
          normalized.includes('birthstone')
        );
      }

      getStoneOptionGroups(picker) {
        if (!picker) return [];
        return Array.from(picker.querySelectorAll('.product-form__input')).filter((group) =>
          this.isStoneOptionLabel(this.getOptionGroupLabel(group))
        );
      }

      getSelectedValueFromGroup(optionGroup) {
        if (!optionGroup) return null;
        const checked = optionGroup.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
        const select = optionGroup.querySelector('select');
        return select?.value || null;
      }

      findNativeOptionControl(optionGroup, selection) {
        if (!optionGroup || !selection) return null;

        const radio = Array.from(optionGroup.querySelectorAll('input[type="radio"]')).find((input) =>
          this.matchesSelection(input.value, selection)
        );
        if (radio) return { type: 'radio', element: radio };

        const select = optionGroup.querySelector('select');
        const option = Array.from(select?.options || []).find((item) =>
          this.matchesSelection(item.value, selection)
        );
        if (select && option) return { type: 'select', element: select, option };

        return null;
      }

      syncAllNativeOptions() {
        const selections = this.selections;
        if (!selections.every(Boolean)) return;

        const picker = this.closest('product-info')?.querySelector('variant-selects');
        if (!picker) return;

        const stoneGroups = this.getStoneOptionGroups(picker);
        let changedControl = null;

        selections.forEach((selection, index) => {
          const optionGroup = stoneGroups[index];
          const control = this.findNativeOptionControl(optionGroup, selection);
          if (!control) return;

          if (control.type === 'radio' && !control.element.checked) {
            control.element.checked = true;
            changedControl = control.element;
          }

          if (control.type === 'select' && control.element.value !== control.option.value) {
            control.element.value = control.option.value;
            // The variant picker reads the `selected` attribute, not the selectedness property.
            Array.from(control.element.options).forEach((item) => item.removeAttribute('selected'));
            control.option.setAttribute('selected', 'selected');
            changedControl = control.element;
          }
        });

        // One change event after both options are set → single Dawn fetch with the full combo.
        changedControl?.dispatchEvent(new Event('change', { bubbles: true }));

        const selectedVariant = this.findSelectedVariant();
        if (selectedVariant && this.variantInput && this.variantInput.value !== selectedVariant[0]) {
          this.variantInput.value = selectedVariant[0];
        }
      }

      findSelectedVariant() {
        const selections = this.selections;
        if (!selections.every(Boolean)) return null;

        const picker = this.closest('product-info')?.querySelector('variant-selects');
        const optionGroups = Array.from(picker?.querySelectorAll('.product-form__input') || []);
        const stoneGroups = this.getStoneOptionGroups(picker);
        const stoneIndexes = stoneGroups.map((group) => optionGroups.indexOf(group)).filter((index) => index >= 0);

        return (
          Object.entries(this.variantData).find(([, variant]) => {
            const options = variant.options || [];

            const stonesMatch =
              stoneIndexes.length === selections.length
                ? stoneIndexes.every((optionIndex, selectionIndex) =>
                    this.matchesSelection(options[optionIndex], selections[selectionIndex])
                  )
                : selections.every((selection) =>
                    options.some((option) => this.matchesSelection(option, selection))
                  );

            if (!stonesMatch) return false;

            return optionGroups.every((group, optionIndex) => {
              if (stoneIndexes.includes(optionIndex)) return true;
              const selectedValue = this.getSelectedValueFromGroup(group);
              if (!selectedValue) return true;
              return this.normalizeValue(options[optionIndex]) === this.normalizeValue(selectedValue);
            });
          }) || null
        );
      }

      normalizeImageUrl(url) {
        if (!url) return null;
        if (url.startsWith('//')) return `${window.location.protocol}${url}`;
        return url;
      }

      toSameOriginUrl(url) {
        const normalized = this.normalizeImageUrl(url);
        if (!normalized) return null;

        try {
          const parsed = new URL(normalized, window.location.origin);
          if (parsed.pathname.startsWith('/cdn/')) {
            return `${window.location.origin}${parsed.pathname}${parsed.search}`;
          }
        } catch (error) {
          // Keep the original normalized URL when parsing fails.
        }

        return normalized;
      }

      comboSanitize(value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9.-]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      prefetchCurrentRow() {
        const first = this.selections[0];
        const prefix = this.comboSanitize(this.currentVariant.prefix || this.dataset.imagePrefix);
        if (!first || !prefix) return;

        const month1 = this.comboSanitize(first.value);
        const months = [
          'january',
          'february',
          'march',
          'april',
          'may',
          'june',
          'july',
          'august',
          'september',
          'october',
          'november',
          'december',
        ];

        months.forEach((month2) => {
          const url = this.buildImageUrl(`${prefix}-${month1}-${month2}.jpg`);
          if (!url) return;
          const image = new Image();
          image.decoding = 'async';
          image.src = url;
        });
      }

      observeProductMedia() {
        const productInfo = this.closest('product-info');
        const gallery =
          productInfo?.querySelector('media-gallery, .product__media-list, .product__media-wrapper') || productInfo;
        if (!gallery || typeof MutationObserver === 'undefined') return;

        this.mediaObserver = new MutationObserver(() => {
          if (this.applyingPreview || !this.isComplete || !this.lastShownImageUrl) return;
          const media =
            productInfo.querySelector('.product__media-item.is-active .media') ||
            productInfo.querySelector('.product__media-item .media');
          const current = media?.querySelector('.birthstone-main-custom-image');
          if (current && current.src === this.lastShownImageUrl) return;
          this.showCombinationImage(this.lastShownImageUrl);
        });
        this.mediaObserver.observe(gallery, { childList: true, subtree: true });
      }

      async updateCombinationImage() {
        if (!this.isComplete) return;

        const sequence = ++this.requestSequence;
        const combinationUrl = this.toSameOriginUrl(this.buildImageUrl(this.buildFilename()));

        if (!combinationUrl) {
          if (this.status) this.status.textContent = this.dataset.configurationMessage || '';
          return;
        }

        this.showCombinationImage(combinationUrl);
        if (this.status) this.status.textContent = '';

        try {
          await this.preloadImage(combinationUrl);
          if (sequence !== this.requestSequence) return;
          this.showCombinationImage(combinationUrl);
        } catch (error) {
          if (sequence !== this.requestSequence) return;
          if (this.status) this.status.textContent = this.dataset.imageErrorMessage || '';
        }
      }

      preloadImage(url) {
        return new Promise((resolve, reject) => {
          const image = new Image();
          const timer = setTimeout(() => {
            image.onload = null;
            image.onerror = null;
            reject(new Error('timeout'));
          }, 2000);
          image.decoding = 'async';
          image.onload = () => {
            clearTimeout(timer);
            resolve();
          };
          image.onerror = () => {
            clearTimeout(timer);
            reject(new Error('error'));
          };
          image.src = url;
        });
      }

      showCombinationImage(url) {
        const sameOriginUrl = this.toSameOriginUrl(url) || url;
        this.lastShownImageUrl = sameOriginUrl;
        this.applyingPreview = true;
        const drawerImage = this.query('[data-birthstone-preview-image]');
        const baseImage = this.query('.birthstone-configurator__base-image');

        if (baseImage) {
          if (!baseImage.dataset.originalSrc) {
            baseImage.dataset.originalSrc = baseImage.currentSrc || baseImage.src;
            baseImage.dataset.originalSrcset = baseImage.getAttribute('srcset') || '';
          }
          baseImage.hidden = true;
        }

        if (drawerImage) {
          drawerImage.removeAttribute('srcset');
          drawerImage.removeAttribute('sizes');
          drawerImage.alt = baseImage?.alt || '';
          drawerImage.src = sameOriginUrl;
          drawerImage.hidden = false;
        }

        const productInfo = this.closest('product-info');
        const media =
          productInfo?.querySelector('.product__media-item.is-active .media') ||
          productInfo?.querySelector('.product__media-item .media');

        if (media) {
          media.classList.add('birthstone-media-preview');
          let customImage = media.querySelector('.birthstone-main-custom-image');
          if (!customImage) {
            customImage = document.createElement('img');
            customImage.className = 'birthstone-main-custom-image';
            customImage.alt = '';
            customImage.decoding = 'async';
            media.appendChild(customImage);
          }
          customImage.src = sameOriginUrl;
        }

        if (this.imageProperty) {
          this.imageProperty.value = sameOriginUrl;
          this.imageProperty.disabled = false;
        }

        requestAnimationFrame(() => {
          this.applyingPreview = false;
        });
      }

      removeGalleryOverlay() {
        this.closest('product-info')
          ?.querySelectorAll('.birthstone-main-custom-image')
          .forEach((image) => image.remove());
        this.closest('product-info')
          ?.querySelectorAll('.birthstone-media-preview')
          .forEach((media) => media.classList.remove('birthstone-media-preview'));
      }

      clearCombinationImage(invalidateRequest = true) {
        if (invalidateRequest) this.requestSequence += 1;

        const drawerImage = this.query('[data-birthstone-preview-image]');
        if (drawerImage) {
          drawerImage.hidden = true;
          drawerImage.removeAttribute('src');
          drawerImage.removeAttribute('srcset');
        }

        const baseImage = this.query('.birthstone-configurator__base-image');
        if (baseImage) {
          baseImage.hidden = false;
          if (baseImage.dataset.originalSrc) {
            baseImage.src = baseImage.dataset.originalSrc;
            if (baseImage.dataset.originalSrcset) {
              baseImage.setAttribute('srcset', baseImage.dataset.originalSrcset);
            }
          }
        }

        this.lastShownImageUrl = null;
        this.removeGalleryOverlay();

        if (this.imageProperty) {
          this.imageProperty.value = '';
          this.imageProperty.disabled = true;
        }
      }
    }
  );
}
